// @ts-nocheck
/**
 * NavButton — reusable nav bar button / link.
 *
 * Renders as <a> when href is provided, <button> otherwise.
 * Consistent white-card styling matching the existing WebViewHeader buttons.
 *
 * Props:
 *   icon       — SVG element or any React node (required)
 *   label      — text label string (optional)
 *   showLabel  — if false, renders icon only (default true)
 *   href       — URL; renders as <a> when set
 *   onClick    — click handler
 *   active     — boolean; shows active highlight when true
 *   title      — tooltip / aria-label text
 *   external   — adds target="_blank" rel="noreferrer" when true;
 *                also appends " →" to the label when showLabel=true and label
 *                doesn't already end with an arrow character
 *   style      — additional style overrides on the root element
 *   className  — additional class name(s)
 *   disabled      — dims and disables interaction
 *   externalBadge — shows a small ↗ arrow badge in the top-right corner of the icon
 *                   area when showLabel=false (icon-only mode). Helps distinguish
 *                   external-navigation buttons from same-page actions.
 */
import React from "react";

const font = "'Open Sans', system-ui, sans-serif";

export default function NavButton({
  icon,
  label,
  showLabel = true,
  href,
  onClick,
  active = false,
  title,
  external = false,
  externalBadge = false,
  style,
  className,
  disabled = false,
}) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: showLabel && label ? "5px" : 0,
    padding: "0.55rem 0.9rem",
    textDecoration: "none",
    fontSize: "0.85rem",
    fontWeight: 600,
    fontFamily: font,
    color: active ? "#0055aa" : "#444",
    background: active ? "#e8f0fe" : "#fff",
    borderRadius: "8px",
    border: active
      ? "1px solid rgba(0, 85, 170, 0.25)"
      : "1px solid rgba(122, 128, 145, 0.15)",
    boxShadow: "3px 3px 8px rgba(122, 128, 145, 0.15)",
    flexShrink: 0,
    whiteSpace: "nowrap",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.45 : 1,
    pointerEvents: disabled ? "none" : "auto",
    lineHeight: 1,
    WebkitTapHighlightColor: "transparent",
    minHeight: "44px",
    ...style,
  };

  const displayLabel = showLabel && label
    ? (external && !externalBadge && !label.endsWith("→") && !label.endsWith("↗") ? `${label} →` : label)
    : null;

  const content = (
    <>
      {icon}
      {displayLabel && <span>{displayLabel}</span>}
    </>
  );

  const badge = (
    <span
      style={{
        position: "absolute",
        top: -4,
        right: -4,
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: "#0066cc",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "7px",
        fontWeight: 700,
        lineHeight: 1,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      ↗
    </span>
  );

  const showBadge = externalBadge;

  if (href) {
    const anchor = (
      <a
        href={disabled ? undefined : href}
        onClick={onClick}
        title={title}
        aria-label={title || label}
        aria-pressed={active || undefined}
        className={showBadge ? undefined : className}
        style={base}
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {content}
      </a>
    );
    if (showBadge) {
      return (
        <span
          className={className}
          style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
        >
          {anchor}
          {badge}
        </span>
      );
    }
    return anchor;
  }

  const btn = (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={title || label}
      aria-pressed={active}
      className={showBadge ? undefined : className}
      style={base}
      disabled={disabled}
    >
      {content}
    </button>
  );

  if (showBadge) {
    return (
      <span
        className={className}
        style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
      >
        {btn}
        {badge}
      </span>
    );
  }

  return btn;
}
